import { Modal, RichTextEditor } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, apiBase } from '../../lib/api.ts'
import { webTokenStorage } from '../../lib/api.ts'
import { useWiMutation } from './api.ts'

/**
 * O "i" ao lado de um campo do item (2026-09-01): abre uma modal com as
 * INSTRUÇÕES de preenchimento daquele campo.
 *
 * A instrução efetiva é resolvida em camadas: personalizada NO ITEM
 * (work_item_field_help) → padrão do cadastro (config.help do campo
 * complementar, ou o texto do catálogo para campos de sistema) → vazio. O
 * lápis abre o editor avançado — o mesmo da Descrição — e grava no item;
 * limpar o conteúdo volta ao padrão.
 */
export function CampoInfo({
  chave,
  titulo,
  ajudaPadrao,
  workItemId,
  fieldHelp,
}: {
  /** "prop:<campo>" para os de sistema; a key do complementar para os demais. */
  chave: string
  titulo: string
  /** Instrução padrão (HTML) — do cadastro do campo ou do catálogo. */
  ajudaPadrao?: string | null
  workItemId: string
  /** Mapa de instruções personalizadas do item ({ chave: html }). */
  fieldHelp: Record<string, string> | undefined
}) {
  const [aberta, setAberta] = useState(false)
  const [editando, setEditando] = useState(false)
  const [editandoTitulo, setEditandoTitulo] = useState(false)

  const doItem = fieldHelp?.[chave]
  const efetiva = doItem ?? ajudaPadrao ?? ''
  /**
   * Titulo proprio da informacao (2026-09-01): o NOME INTEIRO da janela
   * (inclusive o "Instruções — ") e editavel; guardado no MESMO mapa do item,
   * sob a chave `titulo:<chave>` — viaja com o template junto do resto.
   */
  const tituloDoItem = fieldHelp?.[`titulo:${chave}`]
  const tituloPadrao = `Instruções — ${titulo}`
  const tituloCompleto = tituloDoItem?.trim() ? tituloDoItem : tituloPadrao

  const salvar = useWiMutation(workItemId, async (html: string) =>
    api['work-items'][':id']['field-help'][':chave'].$post({
      param: { id: workItemId, chave },
      json: { html },
    }),
  )
  const salvarTitulo = useWiMutation(workItemId, async (texto: string) =>
    api['work-items'][':id']['field-help'][':chave'].$post({
      param: { id: workItemId, chave: `titulo:${chave}` },
      json: { html: texto },
    }),
  )
  /** Confirma o titulo digitado (Enter ou sair do campo) uma unica vez. */
  const confirmouTitulo = useRef(false)
  const confirmarTitulo = (bruto: string) => {
    if (confirmouTitulo.current) return
    confirmouTitulo.current = true
    setEditandoTitulo(false)
    const novo = bruto.trim()
    if (novo === '' || novo === tituloCompleto) return
    // voltar ao nome padrao remove a personalizacao
    salvarTitulo.mutate(novo === tituloPadrao ? '' : novo)
  }
  const iniciarTitulo = () => {
    confirmouTitulo.current = false
    setEditandoTitulo(true)
  }

  /**
   * HINT de nota no hover do "i" (2026-09-01): mostra o conteudo sem clicar;
   * rola se for longo (da para levar o mouse ate a nota) e fecha ao sair.
   */
  const [hint, setHint] = useState<{ left: number; top: number; acima: boolean } | null>(null)
  const hintFecha = useRef<number | undefined>(undefined)
  const abrirHint = (e: { currentTarget: Element }) => {
    if (aberta || efetiva.trim() === '') return
    window.clearTimeout(hintFecha.current)
    const r = e.currentTarget.getBoundingClientRect()
    const acima = r.bottom + 280 > window.innerHeight && r.top > 300
    setHint({
      left: Math.max(8, Math.min(r.left - 12, window.innerWidth - 336)),
      top: acima ? r.top - 8 : r.bottom + 8,
      acima,
    })
  }
  const agendarFecharHint = () => {
    window.clearTimeout(hintFecha.current)
    hintFecha.current = window.setTimeout(() => setHint(null), 140)
  }
  const segurarHint = () => window.clearTimeout(hintFecha.current)

  return (
    <>
      <button
        type="button"
        className="wi-info"
        aria-label={`Instruções de ${titulo}`}
        onClick={(e) => {
          e.stopPropagation()
          setHint(null)
          setEditando(false)
          setAberta(true)
        }}
        onMouseEnter={abrirHint}
        onMouseLeave={agendarFecharHint}
      >
        <i className="fas fa-info-circle" aria-hidden />
      </button>

      {hint
        ? createPortal(
            <div
              className={`wi-info__hint${hint.acima ? ' wi-info__hint--acima' : ''}`}
              style={{ left: hint.left, top: hint.top }}
              onMouseEnter={segurarHint}
              onMouseLeave={agendarFecharHint}
            >
              <div className="wi-info__hint-titulo">{tituloCompleto}</div>
              {/* eslint-disable-next-line react/no-danger */}
              <div className="ob-rte__view wi-info__hint-corpo desc-scroll" dangerouslySetInnerHTML={{ __html: efetiva }} />
            </div>,
            document.body,
          )
        : null}

      {aberta ? (
        <Modal
          open
          onClose={() => setAberta(false)}
          title={tituloCompleto}
          titleNode={
            // em modo edicao, clicar no titulo troca o NOME INTEIRO da janela
            editando ? (
              editandoTitulo ? (
                <input
                  className="wi-info__tituloinput"
                  autoFocus
                  defaultValue={tituloCompleto}
                  aria-label="Título da informação"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmarTitulo((e.target as HTMLInputElement).value)
                  }}
                  onBlur={(e) => confirmarTitulo(e.target.value)}
                />
              ) : (
                <button
                  type="button"
                  className="wi-info__titulobtn"
                  title="Clique para alterar o título desta informação"
                  onClick={iniciarTitulo}
                >
                  {tituloCompleto}
                </button>
              )
            ) : (
              tituloCompleto
            )
          }
          width={640}
          resizable
          sizeKey="campo-info"
          footer={null}
          headerExtra={
            // o lapis fica ao lado do X (pedido 2026-09-01)
            !editando ? (
              <button
                type="button"
                className="wi-info__lapis"
                title="Editar as instruções deste item"
                aria-label="Editar as instruções deste item"
                onClick={() => setEditando(true)}
              >
                <i className="fas fa-pen" aria-hidden />
              </button>
            ) : null
          }
        >
          {editando ? (
            <RichTextEditor
              value={doItem ?? ajudaPadrao ?? ''}
              startEditing
              placeholder="Escreva como este campo deve ser preenchido…"
              minHeight={180}
              onSave={(html) => {
                salvar.mutate(html, { onSuccess: () => setEditando(false) })
              }}
              onEditingChange={(em) => {
                if (!em) setEditando(false)
              }}
            />
          ) : (
            <div className="wi-info__corpo">
              {efetiva.trim() !== '' ? (
                // eslint-disable-next-line react/no-danger
                <div className="ob-rte__view" dangerouslySetInnerHTML={{ __html: efetiva }} />
              ) : (
                <p className="wi-info__vazia">Sem instruções para este campo ainda.</p>
              )}
              {doItem !== undefined || ajudaPadrao ? (
                <div className="wi-info__acoes">
                  {doItem !== undefined ? (
                    <span className="wi-info__origem">Personalizada neste item</span>
                  ) : (
                    <span className="wi-info__origem">Padrão do cadastro</span>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </Modal>
      ) : null}
    </>
  )
}

/** Instruções dos campos de SISTEMA (padrão do core + edições do catálogo). */
export function useSystemFieldHelp() {
  return useQuery({
    queryKey: ['system-field-help'],
    staleTime: 60_000,
    queryFn: async () => {
      const r = await fetch(`${apiBase()}/custom-fields/system`, {
        headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
      })
      const d = (await r.json()) as { systemFields: { key: string; nome: string; icone: string; help: string; editado: boolean }[] }
      return d.systemFields
    },
  })
}
